import React, { useState, useEffect, useCallback } from 'react';
import { GoogleMap, useJsApiLoader, MarkerF, OverlayViewF, PolylineF, CircleF } from '@react-google-maps/api';
import { Ship, Anchor, MapPin, Calendar, Navigation } from 'lucide-react';
import {
    useShipsInArea,
    usePoisInArea,
    useShipPoiEvents,
    useShipVisits,
    useShipPortEvents,
    useShipPositionHistory,
    useFindPathViaPoints
} from '@/core/api/queries';

const containerStyle = {
    width: '100%',
    height: '100vh'
};

const center = {
    lat: 54.5259,
    lng: 18.5212
};

const ShipTrackingMap = () => {
    const [selectedShip, setSelectedShip] = useState(null);
    const [selectedPoi, setSelectedPoi] = useState(null);
    const [selectedWaypoints, setSelectedWaypoints] = useState([]);
    const [showPoiRadiuses, setShowPoiRadiuses] = useState(false);

    const [dateRange, setDateRange] = useState({
        from: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        to: new Date().toISOString().split('T')[0]
    });
    const [map, setMap] = useState(null);
    const [bounds, setBounds] = useState(null);
    const [debouncedBounds, setDebouncedBounds] = useState(null);

    const { isLoaded } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: 'AIzaSyBUfoGJxQTuhIXyfc3k2a9iRf32r8hr8Ug'
    });

    const { data: poiRoute } = useFindPathViaPoints(
        selectedWaypoints[0] || null,
        selectedWaypoints[selectedWaypoints.length - 1] || null,
        selectedWaypoints.slice(1, -1),
        selectedWaypoints.length >= 2 && !!selectedShip
    );


    // Debounced bounds
    useEffect(() => {
        if (!bounds) return;
        const timer = setTimeout(() => setDebouncedBounds(bounds), 800);
        return () => clearTimeout(timer);
    }, [bounds]);

    // React Query hooks
    const { data: ships = [], isLoading: shipsLoading } = useShipsInArea(debouncedBounds, !!debouncedBounds);
    const { data: pois = [] } = usePoisInArea(debouncedBounds, !!debouncedBounds);

    const { data: poiEvents = [], isLoading: poiEventsLoading } = useShipPoiEvents(
        selectedShip?.id,
        20,
        !!selectedShip
    );

    const { data: visits = [] } = useShipVisits(selectedShip?.id, 20, !!selectedShip);
    const { data: portEvents = [] } = useShipPortEvents(selectedShip?.id, 20, !!selectedShip);

    const { data: positionHistory = [] } = useShipPositionHistory(
        selectedShip?.id,
        dateRange,
        !!selectedShip
    );

    const detailsLoading = poiEventsLoading;

    const handleBoundsChanged = useCallback(() => {
        if (!map) return;

        const mapBounds = map.getBounds();
        if (!mapBounds) return;

        const ne = mapBounds.getNorthEast();
        const sw = mapBounds.getSouthWest();

        setBounds({
            topLeftLat: ne.lat(),
            topLeftLng: sw.lng(),
            bottomRightLat: sw.lat(),
            bottomRightLng: ne.lng()
        });
    }, [map]);

    const handleShipClick = (ship) => {
        setSelectedShip(ship);
        setSelectedPoi(null);
        setSelectedWaypoints([]);
    };

    const handlePoiClick = (poi) => {
        setSelectedPoi(poi);
    };

    const handleWaypointClick = (poiCode) => {
        setSelectedWaypoints(prev => {
            if (prev.includes(poiCode)) {
                return prev.filter(code => code !== poiCode);
            }
            return [...prev, poiCode];
        });
    };

    const clearSelectedWaypoints = () => {
        setSelectedWaypoints([]);
    };


    const getShipIcon = (heading) => {
        const rotation = heading || 0;
        return {
            path: 'M12 2L4 12h16L12 2z',
            fillColor: '#3b82f6',
            fillOpacity: 0.9,
            strokeColor: '#ffffff',
            strokeWeight: 2,
            scale: 1.2,
            rotation: rotation,
            anchor: new window.google.maps.Point(12, 12)
        };
    };

    const getPoiColor = (type) => {
        switch (type) {
            case 'PORT':
            case 'PORT_GEOMETRIC_CENTER':
                return '#1e40af';
            case 'TERMINAL':
                return '#10b981';
            case 'WAYPOINT':
                return '#6b7280';
            default:
                return '#6b7280';
        }
    };

    const waypointColors = [
        '#ef4444', '#f59e0b', '#10b981', '#3b82f6',
        '#8b5cf6', '#ec4899', '#14b8a6', '#f97316',
    ];

    const getWaypointColor = (poiCode) => {
        const index = selectedWaypoints.indexOf(poiCode);
        if (index === -1) return null;
        return waypointColors[index % waypointColors.length];
    };

    const getPositionSegments = (positions) => {
        if (!positions || positions.length < 2) return [];

        const segments = [];
        let currentSegment = [positions[0]];
        const maxDistanceKm = 100;

        for (let i = 1; i < positions.length; i++) {
            const prev = positions[i - 1];
            const curr = positions[i];

            if (!prev.lat || !prev.lng || !curr.lat || !curr.lng) continue;

            const R = 6371;
            const dLat = (curr.lat - prev.lat) * Math.PI / 180;
            const dLng = (curr.lng - prev.lng) * Math.PI / 180;
            const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
                Math.cos(prev.lat * Math.PI / 180) * Math.cos(curr.lat * Math.PI / 180) *
                Math.sin(dLng / 2) * Math.sin(dLng / 2);
            const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
            const distance = R * c;

            if (distance > maxDistanceKm) {
                if (currentSegment.length > 1) {
                    segments.push(currentSegment);
                }
                currentSegment = [curr];
            } else {
                currentSegment.push(curr);
            }
        }

        if (currentSegment.length > 1) {
            segments.push(currentSegment);
        }

        return segments;
    };

    const formatDate = (date) => {
        if (!date) return 'N/A';
        return new Date(date).toLocaleString();
    };

    if (!isLoaded) {
        return <div className="flex items-center justify-center h-screen">Loading map...</div>;
    }

    const shipDetails = {
        poiEvents: poiEvents || [],
        visits: visits || [],
        portEvents: portEvents || []
    };

    return (
        <div className="relative w-full h-screen">
            <GoogleMap
                mapContainerStyle={containerStyle}
                center={center}
                zoom={10}
                onLoad={setMap}
                onBoundsChanged={handleBoundsChanged}
                onDragEnd={handleBoundsChanged}
                onZoomChanged={handleBoundsChanged}
            >
                {ships.map((ship) => (
                    ship.lastLat && ship.lastLon && (
                        <MarkerF
                            key={ship.id}
                            position={{ lat: ship.lastLat, lng: ship.lastLon }}
                            icon={getShipIcon(ship.lastHeading)}
                            onClick={() => handleShipClick(ship)}
                            title={ship.name}
                        />
                    )
                ))}

                {pois.map((poi) => {
                    const waypointColor = getWaypointColor(poi.code);
                    const poiColor = waypointColor || getPoiColor(poi.type);
                    const isSelected = selectedPoi?.id === poi.id;
                    const isWaypoint = waypointColor !== null;

                    return (
                        <React.Fragment key={poi.id}>
                            {(showPoiRadiuses || isSelected || isWaypoint) && (
                                <CircleF
                                    center={{ lat: poi.lat, lng: poi.lng }}
                                    radius={poi.radiusM || 500}
                                    options={{
                                        fillColor: poiColor,
                                        fillOpacity: isWaypoint ? 0.25 : 0.15,
                                        strokeColor: poiColor,
                                        strokeWeight: isWaypoint ? 2 : 2,
                                        strokeOpacity: isWaypoint ? 0.8 : 0.6,
                                    }}
                                />
                            )}
                            <MarkerF
                                position={{ lat: poi.lat, lng: poi.lng }}
                                icon={{
                                    path: window.google.maps.SymbolPath.CIRCLE,
                                    fillColor: poiColor,
                                    fillOpacity: 1,
                                    strokeColor: '#ffffff',
                                    strokeWeight: 2,
                                    scale: isWaypoint ? 9 : (isSelected ? 8 : 6),
                                }}
                                title={poi.portName || poi.terminalName || poi.code}
                                onClick={() => handlePoiClick(poi)}
                            />
                            {isWaypoint && (
                                <OverlayViewF
                                    position={{ lat: poi.lat, lng: poi.lng }}
                                    mapPaneName="overlayMouseTarget"
                                >
                                    <div
                                        style={{
                                            background: poiColor,
                                            color: 'white',
                                            width: '20px',
                                            height: '20px',
                                            borderRadius: '50%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: '11px',
                                            fontWeight: 'bold',
                                            border: '2px solid white',
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                                            transform: 'translate(-50%, -50%)',
                                            pointerEvents: 'none'
                                        }}
                                    >
                                        {selectedWaypoints.indexOf(poi.code) + 1}
                                    </div>
                                </OverlayViewF>
                            )}
                        </React.Fragment>
                    );
                })}

                {selectedShip && positionHistory.length > 0 &&
                    getPositionSegments(positionHistory).map((segment, segmentIndex) => (
                        <PolylineF
                            key={`position-segment-${segmentIndex}`}
                            path={segment.map(pos => ({ lat: pos.lat, lng: pos.lng }))}
                            options={{
                                strokeColor: '#ef4444',
                                strokeOpacity: 0.6,
                                strokeWeight: 2,
                                geodesic: true,
                            }}
                        />
                    ))
                }

                {selectedShip && poiRoute?.routes && (
                    <>
                        {poiRoute.routes.map((route, routeIndex) => (
                            <PolylineF
                                key={`poi-route-${routeIndex}`}
                                path={route.path}
                                options={{
                                    strokeColor: '#3b82f6',
                                    strokeOpacity: 0.7,
                                    strokeWeight: 3,
                                    geodesic: true,
                                }}
                            />
                        ))}
                        {poiRoute.nodes?.map((node, nodeIndex) => (
                            <MarkerF
                                key={`poi-node-${nodeIndex}`}
                                position={{ lat: node.lat, lng: node.lng }}
                                icon={{
                                    path: window.google.maps.SymbolPath.CIRCLE,
                                    fillColor: '#3b82f6',
                                    fillOpacity: 0.9,
                                    strokeColor: '#ffffff',
                                    strokeWeight: 2,
                                    scale: 8,
                                }}
                                title={node.port_name || node.city}
                            />
                        ))}
                    </>
                )}

                {selectedPoi && (
                    <OverlayViewF
                        position={{ lat: selectedPoi.lat, lng: selectedPoi.lng }}
                        mapPaneName="floatPane"
                    >
                        <div
                            style={{
                                background: 'white',
                                padding: '12px',
                                borderRadius: '8px',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                                minWidth: '200px',
                                transform: 'translate(-50%, -120%)',
                                pointerEvents: 'auto',
                                zIndex: 2000
                            }}
                        >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '8px' }}>
                                <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: '#1f2937' }}>
                                    {selectedPoi.portName || selectedPoi.terminalName || selectedPoi.code}
                                </h3>
                                <button
                                    onClick={() => setSelectedPoi(null)}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                        fontSize: '18px',
                                        color: '#6b7280',
                                        padding: 0,
                                        marginLeft: '8px',
                                        lineHeight: 1
                                    }}
                                >
                                    ×
                                </button>
                            </div>
                            <div style={{ fontSize: '12px', color: '#6b7280', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                <div><strong>Code:</strong> {selectedPoi.code}</div>
                                <div><strong>Type:</strong> {selectedPoi.type}</div>
                                {selectedPoi.locode && <div><strong>LOCODE:</strong> {selectedPoi.locode}</div>}
                                {selectedPoi.city && <div><strong>City:</strong> {selectedPoi.city}</div>}
                                {selectedPoi.country && <div><strong>Country:</strong> {selectedPoi.country}</div>}
                                <div><strong>Radius:</strong> {selectedPoi.radiusM}m</div>
                                <div style={{ fontSize: '11px', color: '#9ca3af' }}>
                                    {selectedPoi.lat.toFixed(6)}, {selectedPoi.lng.toFixed(6)}
                                </div>
                            </div>
                        </div>
                    </OverlayViewF>
                )}
            </GoogleMap>

            {ships.length > 0 && (
                <div
                    style={{
                        position: 'fixed',
                        bottom: '20px',
                        left: '20px',
                        background: 'rgba(0, 0, 0, 0.7)',
                        color: 'white',
                        padding: '8px 16px',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: '600',
                        zIndex: 1000
                    }}
                >
                    {ships.length} ship{ships.length !== 1 ? 's' : ''} • {pois.length} POI{pois.length !== 1 ? 's' : ''}
                </div>
            )}

            {shipsLoading && (
                <div className="absolute top-4 right-4 bg-white rounded-lg shadow-lg px-4 py-2">
                    <div className="flex items-center gap-2">
                        <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full" />
                        <span className="text-sm text-gray-600">Loading ships...</span>
                    </div>
                </div>
            )}

            {selectedShip && (
                <div className="absolute top-0 right-0 w-96 h-full bg-white shadow-xl flex flex-col overflow-hidden">
                    <div className="p-4 border-b bg-gray-50 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <Ship size={24} className="text-blue-600" />
                            <h2 className="text-xl font-bold text-gray-800">Ship Details</h2>
                        </div>
                        <button
                            onClick={() => setSelectedShip(null)}
                            className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
                        >
                            ×
                        </button>
                    </div>

                    <div className="flex-1 overflow-y-auto p-4">
                        {detailsLoading ? (
                            <div className="flex items-center justify-center py-8">
                                <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full" />
                            </div>
                        ) : (
                            <div className="space-y-6">
                                <div className="space-y-3">
                                    <h3 className="font-bold text-lg text-gray-800">{selectedShip.name}</h3>

                                    <div className="grid grid-cols-2 gap-2 text-sm">
                                        <div>
                                            <span className="text-gray-500">IMO:</span>
                                            <span className="ml-2 font-medium">{selectedShip.id}</span>
                                        </div>
                                        {selectedShip.mmsi && (
                                            <div>
                                                <span className="text-gray-500">MMSI:</span>
                                                <span className="ml-2 font-medium">{selectedShip.mmsi}</span>
                                            </div>
                                        )}
                                        {selectedShip.shipType && (
                                            <div className="col-span-2">
                                                <span className="text-gray-500">Type:</span>
                                                <span className="ml-2 font-medium">{selectedShip.shipType}</span>
                                            </div>
                                        )}
                                        {selectedShip.operatorName && (
                                            <div className="col-span-2">
                                                <span className="text-gray-500">Operator:</span>
                                                <span className="ml-2 font-medium">{selectedShip.operatorName}</span>
                                            </div>
                                        )}
                                    </div>

                                    <div className="flex items-center gap-2">
                                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${selectedShip.status === 'AT_SEA'
                                            ? 'bg-blue-100 text-blue-800'
                                            : 'bg-green-100 text-green-800'
                                            }`}>
                                            {selectedShip.status || 'UNKNOWN'}
                                        </span>
                                        {selectedShip.isStationary && (
                                            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-800">
                                                STATIONARY
                                            </span>
                                        )}
                                    </div>

                                    {selectedShip.currentPoiCode && (
                                        <div className="flex items-start gap-2 p-3 bg-blue-50 rounded-lg">
                                            <MapPin size={16} className="text-blue-600 mt-0.5" />
                                            <div className="text-sm">
                                                <div className="font-semibold text-gray-800">{selectedShip.currentPoiCode}</div>
                                                <div className="text-gray-600">{selectedShip.currentPoiType}</div>
                                            </div>
                                        </div>
                                    )}

                                    {selectedShip.lastDestination && (
                                        <div className="flex items-start gap-2 p-3 bg-orange-50 rounded-lg">
                                            <Navigation size={16} className="text-orange-600 mt-0.5" />
                                            <div className="text-sm">
                                                <div className="text-gray-500">Destination</div>
                                                <div className="font-semibold text-gray-800">{selectedShip.lastDestination}</div>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div className="border-t pt-4">
                                    <div className="flex items-center justify-between mb-3">
                                        <h4 className="font-semibold text-gray-700">Position History</h4>
                                        <button
                                            onClick={() => setShowPoiRadiuses(!showPoiRadiuses)}
                                            className={`px-3 py-1 text-xs rounded transition-colors ${showPoiRadiuses
                                                ? 'bg-blue-500 text-white'
                                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                                }`}
                                        >
                                            {showPoiRadiuses ? 'Hide' : 'Show'} POI Radiuses
                                        </button>
                                    </div>
                                    <div className="space-y-3">
                                        <div className="grid grid-cols-2 gap-2">
                                            <div>
                                                <label className="text-xs text-gray-600 block mb-1">From:</label>
                                                <input
                                                    type="date"
                                                    value={dateRange.from}
                                                    onChange={(e) => setDateRange(prev => ({ ...prev, from: e.target.value }))}
                                                    className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                />
                                            </div>
                                            <div>
                                                <label className="text-xs text-gray-600 block mb-1">To:</label>
                                                <input
                                                    type="date"
                                                    value={dateRange.to}
                                                    onChange={(e) => setDateRange(prev => ({ ...prev, to: e.target.value }))}
                                                    className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                />
                                            </div>
                                        </div>
                                        <div className="text-xs text-gray-500">
                                            {positionHistory.length} positions
                                        </div>

                                        {selectedWaypoints.length > 0 && (
                                            <div className="flex items-center justify-between gap-2 text-xs">
                                                <span className="text-gray-600">
                                                    {selectedWaypoints.length} waypoint{selectedWaypoints.length !== 1 ? 's' : ''} selected
                                                </span>
                                                <button
                                                    onClick={clearSelectedWaypoints}
                                                    className="text-blue-600 hover:text-blue-700 underline"
                                                >
                                                    Clear
                                                </button>
                                            </div>
                                        )}

                                        <div className="space-y-2 text-xs">
                                            <div className="flex items-center gap-2">
                                                <div className="w-8 h-0.5 bg-red-500 opacity-60" />
                                                <span className="text-gray-600">Actual positions</span>
                                            </div>
                                            {selectedWaypoints.length >= 2 && (
                                                <div className="flex items-center gap-2">
                                                    <div className="w-8 h-0.5 bg-blue-500 opacity-70" />
                                                    <span className="text-gray-600">Route through waypoints</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {shipDetails.poiEvents.length > 0 && (
                                    <div>
                                        <h4 className="font-semibold text-gray-700 mb-2 flex items-center gap-2">
                                            <Anchor size={18} />
                                            POI Events
                                            {selectedWaypoints.length === 0 && (
                                                <span className="text-xs text-gray-500">
                                                    (Click to select route waypoints)
                                                </span>
                                            )}
                                        </h4>
                                        <div className="space-y-2">
                                            {shipDetails.poiEvents.map((event) => {
                                                const poiCode = event.arrivedPoiCode || event.departedPoiCode;
                                                const waypointIndex = poiCode ? selectedWaypoints.indexOf(poiCode) : -1;
                                                const isSelected = waypointIndex !== -1;
                                                const waypointColor = isSelected ? waypointColors[waypointIndex % waypointColors.length] : null;

                                                return (
                                                    <div
                                                        key={event.id}
                                                        onClick={() => {
                                                            if (poiCode) {
                                                                handleWaypointClick(poiCode);
                                                            }
                                                        }}
                                                        className={`p-3 border rounded-lg text-sm transition-all ${poiCode
                                                            ? 'cursor-pointer hover:border-blue-400 hover:bg-blue-50'
                                                            : ''
                                                            } ${isSelected
                                                                ? 'border-2'
                                                                : 'border-gray-200'
                                                            }`}
                                                        style={isSelected ? {
                                                            borderColor: waypointColor,
                                                            backgroundColor: `${waypointColor}15`
                                                        } : {}}
                                                    >
                                                        <div className="flex items-start justify-between">
                                                            <div className="flex-1">
                                                                <div className="font-medium text-gray-800">{event.type}</div>
                                                                {event.arrivedPoiCode && (
                                                                    <div className="text-gray-600">Arrived: {event.arrivedPoiCode}</div>
                                                                )}
                                                                {event.poiName && (
                                                                    <div className="text-gray-600 text-bold">{event.poiName}</div>
                                                                )}
                                                                {event.departedPoiCode && (
                                                                    <div className="text-gray-600">Departed: {event.departedPoiCode}</div>
                                                                )}
                                                                {event.distanceToPoiMeters && (
                                                                    <div className="text-gray-500 text-xs">
                                                                        Distance: {(event.distanceToPoiMeters / 1000).toFixed(2)} km
                                                                    </div>
                                                                )}
                                                                <div className="text-gray-400 text-xs mt-1">
                                                                    {formatDate(event.type.includes('DEPARTURE') ? event.atd : event.ata)}
                                                                </div>
                                                            </div>
                                                            {isSelected && (
                                                                <div
                                                                    className="ml-2 flex items-center justify-center w-6 h-6 text-white rounded-full text-xs font-bold"
                                                                    style={{ backgroundColor: waypointColor }}
                                                                >
                                                                    {waypointIndex + 1}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}

                                {shipDetails.visits.length > 0 && (
                                    <div>
                                        <h4 className="font-semibold text-gray-700 mb-2 flex items-center gap-2">
                                            <MapPin size={18} />
                                            Port Visits
                                        </h4>
                                        <div className="space-y-2">
                                            {shipDetails.visits.map((visit) => (
                                                <div key={visit.id} className="p-3 border border-gray-200 rounded-lg text-sm">
                                                    <div className="font-medium text-gray-800">{visit.eventType}</div>
                                                    {visit.port && (
                                                        <div className="text-gray-700">{visit.port.name}</div>
                                                    )}
                                                    {visit.terminal && (
                                                        <div className="text-gray-600">Terminal: {visit.terminal.name}</div>
                                                    )}
                                                    {visit.destination && (
                                                        <div className="text-gray-600">Dest: {visit.destination}</div>
                                                    )}
                                                    <div className="text-gray-400 text-xs mt-1">
                                                        {formatDate(visit.createdAt)}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {shipDetails.portEvents.length > 0 && (
                                    <div>
                                        <h4 className="font-semibold text-gray-700 mb-2 flex items-center gap-2">
                                            <Calendar size={18} />
                                            Port Schedules
                                        </h4>
                                        <div className="space-y-2">
                                            {shipDetails.portEvents.map((event) => (
                                                <div key={event.id} className="p-3 border border-gray-200 rounded-lg text-sm">
                                                    {event.port && (
                                                        <div className="font-medium text-gray-800">{event.port.name}</div>
                                                    )}
                                                    {event.eta && (
                                                        <div className="text-gray-600">ETA: {formatDate(event.eta)}</div>
                                                    )}
                                                    {event.etd && (
                                                        <div className="text-gray-600">ETD: {formatDate(event.etd)}</div>
                                                    )}
                                                    <div className="text-gray-400 text-xs mt-1">
                                                        Source: {event.source}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {shipDetails.poiEvents.length === 0 &&
                                    shipDetails.visits.length === 0 &&
                                    shipDetails.portEvents.length === 0 && (
                                        <div className="text-center py-8 text-gray-500">
                                            No additional data available for this ship
                                        </div>
                                    )}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ShipTrackingMap;